import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const FilterPanel = ({ filters, forms, labels, onFilterChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [localFilters, setLocalFilters] = useState(filters);

  const statusOptions = [
    { value: 'new', label: 'New' },
    { value: 'reviewed', label: 'Reviewed' },
    { value: 'archived', label: 'Archived' },
    { value: 'flagged', label: 'Flagged' }
  ];

  const formOptions = forms?.map(f => ({ value: f?.id, label: f?.title }));
  const labelOptions = labels?.map(l => ({ value: l?.id, label: l?.name }));

  const handleFilterUpdate = (key, value) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
  };

  const handleApplyFilters = () => {
    onFilterChange(localFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      formId: null,
      status: null,
      dateFrom: null,
      dateTo: null,
      labelId: null,
      searchTerm: ''
    };
    setLocalFilters(clearedFilters);
    onFilterChange(clearedFilters);
  };

  const activeFilterCount = Object.values(localFilters)?.filter(v => v !== null && v !== '')?.length;

  return (
    <div className="bg-card border border-border rounded-lg mb-6">
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Icon name="Filter" size={20} className="text-muted-foreground" />
          <h3 className="text-sm font-semibold text-foreground">Filters</h3>
          {activeFilterCount > 0 && (
            <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-primary text-primary-foreground text-xs font-medium">
              {activeFilterCount}
            </span>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          iconName={isExpanded ? 'ChevronUp' : 'ChevronDown'}
          iconPosition="right"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? 'Hide' : 'Show'} Filters
        </Button>
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 border-t border-border pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Search
              </label>
              <Input
                type="search"
                placeholder="Search submissions..."
                value={localFilters?.searchTerm || ''}
                onChange={(e) => handleFilterUpdate('searchTerm', e?.target?.value)}
              />
            </div>

            {/* Form Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Form
              </label>
              <Select
                options={formOptions}
                value={localFilters?.formId}
                placeholder="All forms"
                clearable
                onChange={(value) => handleFilterUpdate('formId', value)}
              />
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Status
              </label>
              <Select
                options={statusOptions}
                value={localFilters?.status}
                placeholder="All statuses"
                clearable
                onChange={(value) => handleFilterUpdate('status', value)}
              />
            </div>

            {/* Date From */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                From Date
              </label>
              <Input
                type="date"
                value={localFilters?.dateFrom || ''}
                onChange={(e) => handleFilterUpdate('dateFrom', e?.target?.value)}
              />
            </div>

            {/* Date To */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                To Date
              </label>
              <Input
                type="date"
                value={localFilters?.dateTo || ''}
                onChange={(e) => handleFilterUpdate('dateTo', e?.target?.value)}
              />
            </div>

            {/* Label Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Label
              </label>
              <Select
                options={labelOptions}
                value={localFilters?.labelId}
                placeholder="All labels"
                clearable
                onChange={(value) => handleFilterUpdate('labelId', value)}
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="default"
              iconName="Search"
              iconPosition="left"
              onClick={handleApplyFilters}
            >
              Apply Filters
            </Button>
            <Button
              variant="outline"
              iconName="X"
              iconPosition="left"
              onClick={handleClearFilters}
            >
              Clear All
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterPanel;